import type { operations } from "@/openapi.gen.ts";
import { $api } from "@/utils/fetch.ts";
import {
	type GetNextPageParamFunction,
	keepPreviousData,
} from "@tanstack/react-query";
import type { SearchSnippetSegment } from "@/shared/search-snippet.ts";

export type AccessLevel = "private" | "project" | "public";

export type ArtifactKind =
	| "document"
	| "presentation"
	| "canvas"
	| "bi_dashboard";

/** Deck IR — derived from the generated API types (single source of truth). */
export type Deck =
	operations["api_spaces_spaceId_artifacts_artifactId_deck_get"]["responses"]["200"]["content"]["application/json"]["deck"];

/** Canvas IR — derived from the generated API types (single source of truth). */
export type Canvas =
	operations["api_spaces_spaceId_artifacts_artifactId_canvas_get"]["responses"]["200"]["content"]["application/json"]["canvas"];
export type DeckSlide = Deck["slides"][number];
export type DeckTheme = NonNullable<Deck["theme"]>;
export type DeckChart = Extract<DeckSlide, { layout: "chart" }>["chart"];
export type DeckColumn = Extract<DeckSlide, { layout: "two-column" }>["left"];

export interface ArtifactWithSpace {
	id: string;
	name: string;
	kind: ArtifactKind;
	spaceId: string;
	spaceName: string;
	createdAt: string;
	updatedAt: string;
	creatorName?: string | null;
	shared: boolean;
	accessLevel?: AccessLevel;
	shareToken?: string | null;
	/** Canvas only: whether public submissions (unauthenticated inserts) are enabled. */
	publicWrites?: boolean;
	isOwner: boolean;
	/** The server's read rule: false for another member's private draft. */
	canOpen: boolean;
	/** Readable but locked by its private chat: only its creator and admins edit. */
	editLockedByChat?: boolean;
	updatedByName?: string | null;
	createdBy?: string | null;
	forkedFromName?: string | null;
	/** The passage a content search matched, in runs; see `SearchSnippet`. */
	snippet?: SearchSnippetSegment[];
}

export interface ArtifactCounts {
	totalArtifacts: number;
	totalProjects: number;
}

// Simple query for fetching all artifacts (used by artifact provider)
export const allArtifactsQueryOptions = (
	spaceId?: string,
	ownerOrShared?: boolean,
) =>
	$api.queryOptions("get", "/api/spaces/artifacts", {
		params: {
			query: { spaceId, ownerOrShared },
		},
	});

// Artifacts scoped to a project for the mentions palette: the project's OWN space
// plus the spaces linked to it via space_links, scoped to spaces the caller can
// read. Mirrors the `projectSpaceId` scoping used by the references endpoints.
export const projectScopedArtifactsQueryOptions = (projectSpaceId: string) =>
	$api.queryOptions("get", "/api/spaces/artifacts", {
		params: {
			query: { projectSpaceId },
		},
	});

// Dashboards (kind='bi_dashboard') surfaced from the dataset spaces linked to a project
// (via space_links), scoped to the spaces the caller can read. Each returned
// row carries its real dataset `spaceId` (BUG-2: open with that, not projectId).
export const projectLinkedDashboardsQueryOptions = (projectId: string) =>
	$api.queryOptions("get", "/api/spaces/artifacts", {
		params: {
			query: { linkedToProjectId: projectId, kind: "bi_dashboard" },
		},
	});

// Resolve a SINGLE artifact by id across ALL spaces the caller can read (no
// 'project' provider filter). Used by the artifact provider to open a
// cross-space app (kind='app' in a linked dataset space) from a chat surface,
// where only the artifactId is known. The row carries the app's REAL dataset
// `spaceId`, which the panel then passes downstream (BUG-2). Fires only on a
// miss against the space-scoped in-list artifacts.
export const resolveArtifactByIdQueryOptions = (artifactId: string) =>
	$api.queryOptions("get", "/api/spaces/artifacts", {
		params: {
			query: { artifactId },
		},
	});

// Query for artifacts in a specific space
export const spaceArtifactsQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/artifacts", {
		params: {
			path: { spaceId },
		},
	});

// Artifacts Queries and Mutations
export const artifactsQueryOptions = ({
	page = 0,
	pageSize,
	search,
	spaceId,
}: {
	page?: number;
	pageSize?: number;
	search?: string;
	spaceId?: string;
}) =>
	$api.queryOptions("get", "/api/spaces/artifacts", {
		params: {
			query: {
				page,
				pageSize,
				search,
				spaceId,
			},
		},
	});

export type ArtifactSort = "name" | "createdAt" | "updatedAt";

export const useArtifactsInfiniteQuery = ({
	search,
	spaceId,
	projectSpaceId,
	kind,
	excludeKind,
	sort,
	sortDir,
	enabled = true,
}: {
	search?: string;
	spaceId?: string;
	/** Scope to a project's own space plus the spaces linked to it. */
	projectSpaceId?: string;
	/** Show only this kind. */
	kind?: ArtifactKind;
	/**
	 * Hide this kind. Server-side rather than a `.filter()` on the page: a
	 * filtered page would keep an arbitrary number of its 20 rows while
	 * `hasNextPage` still described the unfiltered set, so "Show more" would
	 * page through mostly-empty results.
	 */
	excludeKind?: ArtifactKind;
	sort?: ArtifactSort;
	sortDir?: "asc" | "desc";
	/** The mention palette only fetches once it is open. */
	enabled?: boolean;
}) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		{ artifacts: unknown[] } & ArtifactCounts & {
				hasNextPage?: boolean;
			}
	> = (lastPage, _allPages, lastPageParam) => {
		if (lastPage.hasNextPage && typeof lastPageParam === "number") {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/spaces/artifacts",
		{
			params: {
				query: {
					pageSize: 20,
					search,
					spaceId,
					projectSpaceId,
					kind,
					excludeKind,
					sort,
					sortDir,
				},
			},
		},
		{
			pageParamName: "page",
			getNextPageParam,
			initialPageParam: 0,
			refetchOnWindowFocus: true,
			enabled,
			// Typing in a search box changes the query key. Without this every
			// keystroke empties the list for a round trip instead of narrowing it.
			placeholderData: keepPreviousData,
		},
	);
};

export const useAddArtifactMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/artifacts");

export const useDeleteArtifactMutation = () =>
	$api.useMutation("delete", "/api/spaces/{spaceId}/artifacts/{artifactId}");

/** The apps in a project's trash that the caller can restore. */
export const artifactTrashQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/artifacts/trash", {
		params: { path: { spaceId } },
	});

/** Bring an app back from its project's trash. */
export const useRestoreArtifactMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/restore",
	);

export const useTransferArtifactMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/transfer",
	);

export const useShareArtifactMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/artifacts/{artifactId}/share");

// ---------------------------------------------------------------------------
// Canvas custom domains. Five calls, because publishing a canvas on a domain is
// a conversation rather than a toggle: ask what DNS records to create, create
// them, ask us to verify ownership, and then wait for the domain's traffic to
// actually be routed here. `setup` is a GET that reads and writes nothing — the
// verification secret is derived from the artifact and domain, so asking for
// instructions never reserves the domain for you.
//
// TWO INDEPENDENT GATES, and the UI has to show both. `status` says whether the
// zone still proves ownership; `certificateIssued` says whether the deployment
// platform has a certificate and is routing the domain to us. A claim succeeds on
// the first alone — the second usually completes minutes or days later, when
// whoever manages the zone points its A/AAAA records at the gateways — so
// "published" and "live" are genuinely different states.
// ---------------------------------------------------------------------------

/** The domain currently published for a canvas, or `undefined`. */
export const artifactDomainQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/domain",
		{ params: { path: { spaceId, artifactId } } },
	);

/**
 * The DNS records to publish for a candidate domain — the ownership TXT record
 * and the gateway A/AAAA records. `enabled` is the caller's, because a debounced
 * draft domain is not always worth a request.
 */
export const artifactDomainSetupQueryOptions = (
	spaceId: string,
	artifactId: string,
	domain: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/domain/setup",
		{ params: { path: { spaceId, artifactId }, query: { domain } } },
	);

/**
 * Run the live DNS check and publish on a match. NOTE: a failed verification is
 * a SUCCESSFUL request returning `verified: false` — the check ran, the zone said
 * no — so `onSuccess` must branch on `verified` rather than assume it published.
 * `retryable` separates a wrong/absent record from a resolver failure.
 */
export const useClaimArtifactDomainMutation = () =>
	$api.useMutation(
		"put",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/domain",
	);

/**
 * Re-run both checks now, for an owner who has just fixed their DNS. Purely a
 * shortcut: the ownership sweep and the certificate worker run on their own
 * schedule, so a domain comes up whether or not this is ever called.
 */
export const useCheckArtifactDomainMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/domain/check",
	);

export const useRemoveArtifactDomainMutation = () =>
	$api.useMutation(
		"delete",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/domain",
	);

export const useCopyArtifactMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/artifacts/{artifactId}/copy");

export const useRequestArtifactAccessMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/request-access",
	);

export const spaceArtifactDeckQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/deck",
		{
			params: {
				path: { spaceId, artifactId },
			},
		},
	);

export const useSetPresentationDesignMutation = () =>
	$api.useMutation(
		"put",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/design",
	);

/**
 * Bind a deck to a presentation template, or clear the binding with `null`.
 *
 * Separate from the deck itself because it is not deck content: it decides
 * which corporate PowerPoint master an EXPORT is filled into, and so lives on
 * the artifact rather than in the shared Yjs doc.
 */
export const useSetPresentationTemplateMutation = () =>
	$api.useMutation(
		"put",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/presentation-template",
	);

export const useUpdateDeckMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/artifacts/{artifactId}/deck");

export const spaceArtifactCanvasQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/canvas",
		{
			params: {
				path: { spaceId, artifactId },
			},
		},
	);

export const canvasVersionsQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/versions",
		{
			params: {
				path: { spaceId, artifactId },
			},
		},
	);

export const useRestoreCanvasVersionMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/versions/{version}/restore",
	);

export const useMarkCanvasVersionMutation = () =>
	$api.useMutation(
		"put",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/versions/{version}/known-good",
	);

export const spaceArtifactDashboardDataQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/dashboard-data",
		{
			params: {
				path: { spaceId, artifactId },
			},
		},
	);

export const spaceArtifactMarkdownQueryOptions = (
	spaceId: string,
	artifactId: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/markdown",
		{
			params: {
				path: { spaceId, artifactId },
			},
		},
	);

/**
 * One artifact's metadata for its info popover, fetched when it opens. Never
 * fresh from cache: an edit or a share elsewhere changes it, and nothing else
 * invalidates this key.
 */
export const artifactInfoQueryOptions = (spaceId: string, artifactId: string) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/artifacts/{artifactId}/info",
		{ params: { path: { spaceId, artifactId } } },
		{ staleTime: 0 },
	);

/**
 * Fetch a shared artifact.
 *
 * `sessionToken` is a canvas VISITOR session (see `utils/canvas-visitor-session.ts`),
 * sent in its own header so it can never be confused with a real oneAI user
 * token in `authorization`. It is part of the query key on purpose: signing in
 * changes what the endpoint returns — the document itself, when the owner
 * requires login — so the two states must not share a cache entry.
 */
export const publicArtifactQueryOptions = (
	token: string,
	sessionToken?: string | null,
) =>
	$api.queryOptions("get", "/api/public/artifacts/{token}", {
		params: {
			path: { token },
			header: sessionToken ? { "x-canvas-session": sessionToken } : undefined,
		},
	});

export const usePublicArtifactRequestAccessMutation = () =>
	$api.useMutation("post", "/api/public/artifacts/{token}/request-access");
